import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { getConnections, getEntry, getPhotograph, imageUrl } from "../lib/api";
import NoSaveImage from "../components/NoSaveImage";
import Seo from "../components/Seo";

/*
 * Explore: encounter one photograph, then choose one of the questions
 * offered beneath it. The chosen question is the visible bridge to the
 * next photograph.
 *
 * Contextual "← Gallery" only appears when the visitor arrived from the
 * Gallery grid on THIS session hop. Following a question clears it.
 *
 * "← Back" is a subtle wander-trail: every question followed pushes the
 * current photograph onto sessionStorage. Retracing pops the stack and
 * arrives without the bridge choreography (this is a step backward, not
 * a new encounter). The trail is cleared by Gallery entry and by
 * "Start somewhere else".
 */

const easeSlow = [0.2, 0.7, 0.1, 1];
const TRAIL_KEY = "explore-trail";
const TRAIL_MAX = 12;

function readTrail() {
  try {
    const raw = sessionStorage.getItem(TRAIL_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((v) => typeof v === "string") : [];
  } catch {
    return [];
  }
}
function writeTrail(arr) {
  sessionStorage.setItem(TRAIL_KEY, JSON.stringify(arr.slice(-TRAIL_MAX)));
}
function pushTrail(id) {
  if (!id) return;
  const t = readTrail();
  if (t.length && t[t.length - 1] === id) return;
  t.push(id);
  writeTrail(t);
}
function popTrail() {
  const t = readTrail();
  if (!t.length) return null;
  const previous = t.pop();
  writeTrail(t);
  return previous;
}

export default function Explore() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const bridgeQ = location.state?.bridgeQ || null;
  const fromGallery = !!location.state?.fromGallery;
  const viaBackTrail = !!location.state?.viaBackTrail;
  const prefersReducedMotion = useReducedMotion();
  const preloaded = useRef(new Set());

  const [photo, setPhoto] = useState(null);
  const [connections, setConnections] = useState([]);
  const [isEndpoint, setIsEndpoint] = useState(false);

  const [imgLoaded, setImgLoaded] = useState(false);
  const [showBridge, setShowBridge] = useState(!!bridgeQ);
  const [showPhoto, setShowPhoto] = useState(false);
  const [showQuestions, setShowQuestions] = useState(false);
  const [leaving, setLeaving] = useState(null);
  const [trailLen, setTrailLen] = useState(readTrail().length);

  useEffect(() => {
    if (!id) getEntry().then((p) => navigate(`/explore/${p.id}`, { replace: true }));
  }, [id, navigate]);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    setPhoto(null);
    setConnections([]);
    setIsEndpoint(false);
    setImgLoaded(false);
    setShowPhoto(false);
    setShowQuestions(false);
    setLeaving(null);
    setShowBridge(!!location.state?.bridgeQ);
    setTrailLen(readTrail().length);

    Promise.all([getPhotograph(id), getConnections(id)]).then(([p, c]) => {
      if (!alive) return;
      setPhoto(p);
      setConnections(c.connections || []);
      setIsEndpoint(!!c.is_endpoint);
      (c.connections || []).forEach((conn) => {
        const url = imageUrl(conn.thumbnail);
        if (url && !preloaded.current.has(url)) {
          const img = new Image();
          img.src = url;
          preloaded.current.add(url);
        }
      });
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!imgLoaded) return;
    const arrivedViaBridge = !!bridgeQ;
    const t = (full, calm) => (prefersReducedMotion ? calm : full);

    const t1 = setTimeout(() => setShowPhoto(true), 40);
    const bridgeOut = arrivedViaBridge
      ? setTimeout(() => setShowBridge(false), t(1200, 400))
      : null;
    const questionsIn = isEndpoint
      ? null
      : setTimeout(
          () => setShowQuestions(true),
          arrivedViaBridge ? t(2000, 900) : t(900, 400),
        );

    return () => {
      clearTimeout(t1);
      if (bridgeOut) clearTimeout(bridgeOut);
      if (questionsIn) clearTimeout(questionsIn);
    };
  }, [imgLoaded, bridgeQ, isEndpoint, prefersReducedMotion]);

  function follow(c) {
    if (leaving) return;
    setLeaving(c.destination_id);
    const url = imageUrl(c.thumbnail);
    if (url && !preloaded.current.has(url)) {
      const img = new Image();
      img.src = url;
      preloaded.current.add(url);
    }
    setShowPhoto(false);
    setShowQuestions(false);
    // Push current photograph onto the wander trail so "← Back" can
    // retrace this hop later. Following a question is a forward move,
    // so we drop any prior fromGallery/viaBackTrail context.
    if (photo?.id) pushTrail(photo.id);
    setTimeout(
      () => {
        navigate(`/explore/${c.destination_id}`, {
          state: { bridgeQ: c.question },
        });
      },
      prefersReducedMotion ? 180 : 420,
    );
  }

  function goBack() {
    const prev = popTrail();
    setTrailLen(readTrail().length);
    if (!prev) return;
    setShowPhoto(false);
    setShowQuestions(false);
    setTimeout(
      () => {
        // No bridgeQ — a retrace is not a new encounter.
        navigate(`/explore/${prev}`, { state: { viaBackTrail: true } });
      },
      prefersReducedMotion ? 120 : 320,
    );
  }

  async function startElsewhere() {
    // A deliberate reset — leave the previous wander behind.
    sessionStorage.removeItem(TRAIL_KEY);
    setTrailLen(0);
    const p = await getEntry(photo?.id);
    navigate(`/explore/${p.id}`);
  }

  const bridgeText = leaving
    ? connections.find((c) => c.destination_id === leaving)?.question
    : bridgeQ;
  const bridgeIsHeld = !!bridgeText && showBridge;
  const photoOpacity = showPhoto ? (bridgeIsHeld ? 0.32 : 1) : 0;

  return (
    <main
      data-testid="explore-page"
      className="min-h-screen bg-[color:var(--bg)] text-white grain relative"
    >
      {photo && (
        <Seo
          title={photo.title ? `${photo.title} · Explore` : "Explore"}
          description={
            photo.alt ||
            "One photograph at a time, connected by the questions they provoke. Photography by Carol Lines."
          }
          path={`/explore/${photo.id}`}
          image={imageUrl(photo.image)}
          imageAlt={photo.alt || photo.title || "Photograph by Carol Lines"}
          type="article"
        />
      )}
      {/* Top-left return control — Gallery takes priority; otherwise the
          quiet wander-trail retrace if it has any hops. Silent on the
          first photograph of a session. */}
      <AnimatePresence>
        {showPhoto && fromGallery && (
          <motion.div
            key="from-gallery"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.9, ease: easeSlow }}
            className="fixed top-20 left-6 md:left-10 z-30"
          >
            <Link
              to="/gallery"
              data-testid="back-to-gallery"
              className="text-sm md:text-base font-serif-display italic text-white/60 hover:text-white nav-link"
            >
              ← Gallery
            </Link>
          </motion.div>
        )}
        {showPhoto && !fromGallery && trailLen > 0 && (
          <motion.button
            key="wander-back"
            type="button"
            onClick={goBack}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.9, ease: easeSlow }}
            className="fixed top-20 left-6 md:left-10 z-30 text-sm md:text-base font-serif-display italic text-white/55 hover:text-white nav-link"
            data-testid="wander-back-btn"
            aria-label="Retrace to the previous photograph"
          >
            ← Back
          </motion.button>
        )}
      </AnimatePresence>

      <section className="relative min-h-screen flex items-center justify-center pt-24 pb-28 px-4 md:px-10">
        <AnimatePresence mode="wait">
          {photo && (
            <motion.div
              key={photo.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: photoOpacity }}
              exit={{ opacity: 0 }}
              transition={{
                duration: prefersReducedMotion ? 0.25 : 0.9,
                ease: easeSlow,
              }}
              className="w-full flex items-center justify-center"
              data-testid="photo-container"
            >
              <NoSaveImage
                data-testid="explore-photo-image"
                src={imageUrl(photo.image)}
                alt={photo.alt || ""}
                onLoad={() => setImgLoaded(true)}
                className="max-h-[82vh] max-w-full w-auto h-auto object-contain"
                style={{
                  boxShadow:
                    "0 60px 120px -40px rgba(0,0,0,0.75), 0 8px 30px -12px rgba(0,0,0,0.5)",
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {bridgeIsHeld && (
            <motion.div
              key={"bridge-" + bridgeText}
              data-testid="bridge-question"
              initial={
                prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 6 }
              }
              animate={{ opacity: 1, y: 0 }}
              exit={
                prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -4 }
              }
              transition={{
                duration: prefersReducedMotion ? 0.25 : 0.75,
                ease: easeSlow,
              }}
              className="pointer-events-none fixed inset-0 z-20 flex items-center justify-center px-6"
            >
              <div className="relative">
                <div
                  aria-hidden="true"
                  className="absolute inset-0 -inset-x-24 -inset-y-16 bg-[radial-gradient(closest-side,rgba(11,12,14,0.85),rgba(11,12,14,0)_75%)] blur-[2px]"
                />
                <p className="relative font-serif-display italic font-light text-2xl sm:text-3xl md:text-5xl lg:text-6xl leading-[1.15] tracking-tight text-white text-center max-w-4xl drop-shadow-[0_2px_18px_rgba(0,0,0,0.85)]">
                  {bridgeText}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </section>

      {!isEndpoint && (
        <section className="px-6 md:px-10 pb-24">
          <div className="max-w-3xl mx-auto min-h-[220px]">
            <AnimatePresence>
              {showQuestions && (
                <motion.ul
                  key="questions"
                  data-testid="questions-list"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.8, ease: easeSlow }}
                  className="space-y-4 md:space-y-5"
                >
                  {connections.map((c, i) => (
                    <motion.li
                      key={c.destination_id}
                      initial={
                        prefersReducedMotion
                          ? { opacity: 0 }
                          : { opacity: 0, y: 12 }
                      }
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        delay: prefersReducedMotion ? 0 : 0.12 + i * 0.11,
                        duration: prefersReducedMotion ? 0.25 : 0.9,
                        ease: easeSlow,
                      }}
                    >
                      <button
                        data-testid={`question-${i}`}
                        onClick={() => follow(c)}
                        className="group w-full text-left"
                      >
                        <span className="font-serif-display italic font-light text-xl sm:text-2xl md:text-3xl lg:text-[2.15rem] leading-[1.25] tracking-tight text-white/70 group-hover:text-white transition-colors choice-line">
                          {c.question}
                        </span>
                      </button>
                    </motion.li>
                  ))}
                </motion.ul>
              )}
            </AnimatePresence>
          </div>
        </section>
      )}

      {/* Corner controls — quiet. Print request opens Contact with this photo pre-filled. */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: showPhoto ? 1 : 0 }}
        transition={{ duration: 0.9, ease: easeSlow }}
        className="fixed bottom-5 right-5 md:bottom-8 md:right-10 z-30 flex items-center gap-4"
      >
        {photo && (
          <Link
            to={`/contact?photo=${photo.id}`}
            data-testid="explore-contact-link"
            className="text-sm md:text-base font-serif-display italic text-white/55 hover:text-white/95 nav-link"
          >
            Request a print
          </Link>
        )}
        <button
          data-testid="start-elsewhere-btn"
          onClick={startElsewhere}
          className="group flex items-center gap-2 px-4 py-2 text-sm md:text-base text-white/80 hover:text-white font-serif-display italic bg-black/40 hover:bg-black/60 backdrop-blur-md border border-white/10 hover:border-white/25 rounded-full transition-all"
        >
          <span
            aria-hidden="true"
            className="inline-block text-white/60 group-hover:text-white transition-colors"
          >
            ↻
          </span>
          <span>Start somewhere else</span>
        </button>
      </motion.div>
    </main>
  );
}
